import { styled } from "goober";
import { useState } from "react";
import { formatTinesUnits } from "../../../shared/tinesUnits";
import { monthlyTinesUnitsLimit } from "../useSessionBootstrap";
import {
  CursorTooltip,
  TooltipLabel,
  TooltipRow,
  TooltipValue,
} from "../CursorTooltip";
import { HelpTooltip } from "../HelpTooltip";
import { Text } from "../Text";
import { useMonitoringWorkflows } from "./useMonitoringWorkflows";
import { Panel, PanelHeader, PanelSkeleton, TotalRow } from "./workflowsLayout";

const USAGE_FILL = {
  ok: "var(--green-600)",
  warn: "var(--orange-600)",
  critical: "var(--red-600)",
};

function usageStatus(fraction: number): keyof typeof USAGE_FILL {
  if (fraction >= 0.9) return "critical";
  if (fraction >= 0.75) return "warn";
  return "ok";
}

const GaugeContainer = styled("div")`
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 900px) {
    flex: none;
    min-height: 32px;
  }
`;

const GaugeBar = styled("div")`
  width: 6px;
  height: 100%;
  border-radius: 999px;
  background: var(--alpha8);
  display: flex;
  flex-direction: column-reverse;
  overflow: hidden;
  cursor: default;
  transition:
    width 120ms ease,
    height 120ms ease;

  &[data-hovered="true"] {
    width: 12px;
  }

  @media (max-width: 900px) {
    width: 100%;
    height: 6px;
    flex-direction: row;

    &[data-hovered="true"] {
      width: 100%;
      height: 12px;
    }
  }
`;

const GaugeSegment = styled("div")`
  flex-basis: 0;
  min-width: 0;
  min-height: 0;
`;

const Rail = styled("div")`
  flex: 0 0 120px;
  display: flex;
  flex-direction: column;

  @media (max-width: 900px) {
    flex: none;
  }
`;

const RailPanel = styled(Panel)`
  flex: 1;
  padding: 32px 16px;
`;

const TitleRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const BottomLabel = styled("div")`
  text-align: center;
`;

export function TinesUnitsPanel() {
  const { report, loading } = useMonitoringWorkflows();
  const [pointer, setPointer] = useState<{ x: number; y: number } | null>(null);

  const limit = monthlyTinesUnitsLimit();
  if (limit === 0) return null;

  if (loading) {
    return (
      <Rail>
        <PanelSkeleton />
      </Rail>
    );
  }

  const units = report.units;
  if (!units) return null;

  const fraction = units.total / limit;
  const fill = USAGE_FILL[usageStatus(fraction)];
  const percent = Math.round(fraction * 100);
  const headroom = Math.max(0, limit - units.total);
  const breakdown = [
    { label: "Compute", units: units.compute },
    { label: "Storage", units: units.storage },
    { label: "Egress", units: units.egress },
  ];

  return (
    <Rail>
      <RailPanel>
        <PanelHeader style={{ alignItems: "center" }}>
          <TitleRow>
            <Text variant="text-small-md">Tines units</Text>
            <HelpTooltip content="A single measure of your usage this month across compute, storage, and egress, shown against your plan limit." />
          </TitleRow>
          <TotalRow>
            <Text variant="display-md">{percent}%</Text>
          </TotalRow>
        </PanelHeader>

        <GaugeContainer>
          <GaugeBar
            data-hovered={pointer ? "true" : undefined}
            onMouseEnter={(e) => setPointer({ x: e.clientX, y: e.clientY })}
            onMouseMove={(e) => setPointer({ x: e.clientX, y: e.clientY })}
            onMouseLeave={() => setPointer(null)}
          >
            <GaugeSegment style={{ flexGrow: units.total, background: fill }} />
            {headroom > 0 && <GaugeSegment style={{ flexGrow: headroom }} />}
          </GaugeBar>
        </GaugeContainer>

        <BottomLabel>
          <Text variant="text-small" color="var(--text-secondary)">
            {formatTinesUnits(units.total)} / {formatTinesUnits(limit)}
          </Text>
        </BottomLabel>

        {pointer && (
          <CursorTooltip x={pointer.x} y={pointer.y} contentKey="tines-units">
            {breakdown.map((row) => (
              <TooltipRow key={row.label}>
                <TooltipLabel>{row.label}</TooltipLabel>
                <TooltipValue>{formatTinesUnits(row.units)}</TooltipValue>
              </TooltipRow>
            ))}
          </CursorTooltip>
        )}
      </RailPanel>
    </Rail>
  );
}
